import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import type { Auth } from 'firebase/auth';
import { print } from 'graphql';
import type { GraphQLFormattedError } from 'graphql/error';
import { fetchAuthToken } from '../../firebaseIntegration/utils.ts';
import { getRollbar } from '../analytics/rollbar.ts';
import { extractOperationName } from './extractOperationName.ts';
import { GraphQLRequestError } from './graphQLRequest.ts';

// Federated-API only accepts Apigee (MGT) access tokens. The request goes through openaudience-graphql, which
// verifies our Firebase token, exchanges it for an MGT one and passes the query on to Federated-API, so the UI
// sends the same Firebase bearer token as for /api/graphql and never handles an MGT token or instance itself.
export const FEDERATED_API_PATH = '/api/federated/graphql';

// Root of every federated query key: what holds per-user data and has to be dropped when somebody signs in.
export const FEDERATED_QUERY_KEY = QueryKeys.FEDERATED_API;

type GraphQLResponse<TData> = {
  data?: TData;
  errors?: GraphQLFormattedError[];
};

export class FederatedApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'FederatedApiError';
    this.status = status;
  }
}

// The proxy answers its own failures (token exchange, upstream down or timing out) with a non-2xx status and a
// GraphQL-style `{ errors: [{ message }] }` body. That message says what went wrong, so it is kept.
const failureFrom = async (response: Response): Promise<FederatedApiError> => {
  const body = (await response.json().catch(() => undefined)) as GraphQLResponse<unknown> | undefined;
  const message = body?.errors?.[0]?.message ?? response.statusText;

  return new FederatedApiError(`[Federated API error]: ${message} (HTTP ${response.status})`, response.status);
};

// No snackbars (callers decide how to present a failure), but failures still reach Rollbar like in graphQLRequest.
const report = (operationName: string, details: Record<string, unknown>) => {
  getRollbar()?.error('Caught by Federated API request', { operationName, ...details });
};

type CreateFederatedGraphQLRequestArgs = {
  auth: Auth;
};

export const createFederatedGraphQLRequest =
  ({ auth }: CreateFederatedGraphQLRequestArgs) =>
  async <TData, TVariables>(document: TypedDocumentNode<TData, TVariables>, variables?: TVariables): Promise<TData> => {
    const token = await fetchAuthToken({ auth });
    const operationName = extractOperationName(document);

    let response: Response;

    try {
      response = await fetch(`${window.location.origin}${FEDERATED_API_PATH}`, {
        body: JSON.stringify({
          operationName,
          query: print(document),
          variables: variables ?? {},
        }),
        headers: {
          authorization: token ? `Bearer ${token}` : '',
          'content-type': 'application/json',
        },
        method: 'POST',
      });
    } catch (error) {
      report(operationName, { networkError: String(error) });
      throw error;
    }

    if (!response.ok) {
      const failure = await failureFrom(response);
      report(operationName, { networkError: failure.message });
      throw failure;
    }

    const result = (await response.json()) as GraphQLResponse<TData>;

    if (result.errors?.length) {
      report(operationName, { caughtErrors: result.errors });
      throw new GraphQLRequestError(result.errors);
    }

    if (!result.data) {
      throw new Error('Federated API response did not contain data');
    }

    return result.data;
  };
